import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import './PdfViewer.scss';

// Set the worker source to use the local worker file
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.mjs',
    import.meta.url
).toString();

const PdfViewer = ({ pdfUrl }) => {
    const canvasRef = useRef(null);
    const [pdf, setPdf] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [scale, setScale] = useState(0.5);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadPdf = async () => {
            try {
                setError(null);
                const loadingTask = pdfjsLib.getDocument(pdfUrl);
                const pdf = await loadingTask.promise;
                setPdf(pdf);
                setNumPages(pdf.numPages);
            } catch (error) {
                console.error('Error loading PDF:', error);
                setError('Failed to load PDF. Please try again.');
            }
        };

        if (pdfUrl) {
            loadPdf();
        }
    }, [pdfUrl]);

    useEffect(() => {
        const renderPage = async () => {
            if (!pdf || !canvasRef.current) return;

            try {
                const page = await pdf.getPage(currentPage);
                const viewport = page.getViewport({ scale });

                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport
                };

                await page.render(renderContext).promise;
            } catch (error) {
                console.error('Error rendering page:', error);
                setError('Failed to render PDF page. Please try again.');
            }
        };

        renderPage();
    }, [pdf, currentPage, scale]);

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < numPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleZoomIn = () => {
        setScale(scale + 0.1);
    };

    const handleZoomOut = () => {
        if (scale > 0.5) {
            setScale(scale - 0.1);
        }
    };

    if (error) {
        return (
            <div className="pdf-viewer">
                <div className="pdf-error">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="pdf-viewer">
            <div className="pdf-controls">
                <button onClick={handlePreviousPage} disabled={currentPage <= 1}>
                    Previous
                </button>
                <span>
                    Page {currentPage} of {numPages}
                </span>
                <button onClick={handleNextPage} disabled={currentPage >= numPages}>
                    Next
                </button>
                <button onClick={handleZoomIn}>Zoom In</button>
                <button onClick={handleZoomOut}>Zoom Out</button>
            </div>
            <div className="pdf-container">
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

export default PdfViewer;
